import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReactComponent as FilterListIcon } from '../../assets/icons/new/UI/FilterList.svg';

export interface FilterSidebarItem {
    key: string;
    label: string;
    count?: number;
    icon?: React.ReactNode;
    editable?: boolean;
}

export interface FilterSidebarSection {
    id: string;
    title?: string;
    addButton?: boolean;
    items: FilterSidebarItem[];
    summaryItem?: FilterSidebarItem;
    defaultOpen?: boolean;
    /** Скрыть секцию (не рендерить), не удаляя из массива данных */
    hidden?: boolean;
}

interface StocksFilterSidebarProps {
    sections: FilterSidebarSection[];
    activeFilter: string;
    onChange: (key: string) => void;
    onDeleteItem?: (sectionId: string, itemKey: string) => void;
    onAddItem?: (sectionId: string, label: string) => void;
    onAddSet?: (name: string) => void;
    defaultWidth?: number;
    selectionCount?: number;
    /** Заполнить ширину контейнера (для мобильного оверлея) */
    fluid?: boolean;
}

const MIN_WIDTH = 48;
const COMPACT_THRESHOLD = 126;
const ICON_THRESHOLD = 68;
const DEFAULT_WIDTH = 214;

const FilterIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" className={className}>
        <path d="M1 2.5h12M3 7h8M5.5 11.5h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const HamburgerIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" className={className}>
        <path d="M1 3h12M1 7h12M1 11h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const ChevronDown: React.FC<{ open: boolean }> = ({ open }) => (
    <svg
        width="10" height="10" viewBox="0 0 10 10" fill="none"
        className={`shrink-0 transition-transform duration-150 ${open ? '' : '-rotate-90'}`}
    >
        <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const PlusIcon: React.FC = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M5 1v8M1 5h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const CloseIcon: React.FC = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const PencilIcon: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M9.5 2.5l2 2M3 9l-.5 2 2-.5L11.5 4.5l-2-2-6.5 6.5z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const DeleteIcon: React.FC = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

// Inline input used both in the header (add filter set) and in sections (add item)
const InlineAddInput: React.FC<{
    placeholder: string;
    onConfirm: (value: string) => void;
    onCancel: () => void;
}> = ({ placeholder, onConfirm, onCancel }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const val = inputRef.current?.value.trim() ?? '';
            if (val) onConfirm(val);
            else onCancel();
        }
        if (e.key === 'Escape') onCancel();
    };

    return (
        <div className="flex items-center gap-[4px] px-2 py-[4px]">
            <input
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                className="flex-1 min-w-0 h-[22px] text-[11px] px-2 rounded-[3px] border border-[#2255a1] outline-none bg-white text-slate-700 placeholder:text-slate-300"
                onKeyDown={handleKeyDown}
                onBlur={() => onCancel()}
            />
            <button
                className="shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
                onMouseDown={(e) => { e.preventDefault(); onCancel(); }}
                title="Отмена"
            >
                <CloseIcon />
            </button>
        </div>
    );
};

const StocksFilterSidebar: React.FC<StocksFilterSidebarProps> = ({
    sections,
    activeFilter,
    onChange,
    onDeleteItem,
    onAddItem,
    onAddSet,
    defaultWidth = DEFAULT_WIDTH,
    selectionCount,
    fluid = false,
}) => {

    const [width, setWidth] = useState(defaultWidth);
    const [openSections, setOpenSections] = useState<Set<string>>(
        () => new Set(sections.filter(s => s.defaultOpen !== false).map(s => s.id))
    );
    const [expandedSummary, setExpandedSummary] = useState<Set<string>>(
        () => new Set(sections.filter(s => s.summaryItem).map(s => s.id))
    );
    const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(null);
    // 'header' = new filter set form; section id = add item form in that section
    const [addingIn, setAddingIn] = useState<string | null>(null);

    const isResizing = useRef(false);
    const startX = useRef(0);
    const startWidth = useRef(0);

    // < 126px: compact — hide arrows, show strip under open sections, labels still visible
    const isCompact = width < COMPACT_THRESHOLD;
    // < 68px: icon-only — 3 letters or icon, hamburger, no +
    const isIconOnly = width < ICON_THRESHOLD;

    const onMouseDown = useCallback((e: React.MouseEvent) => {
        isResizing.current = true;
        startX.current = e.clientX;
        startWidth.current = width;
        e.preventDefault();

        const onMouseMove = (e: MouseEvent) => {
            if (!isResizing.current) return;
            const delta = e.clientX - startX.current;
            setWidth(Math.max(MIN_WIDTH, startWidth.current + delta));
        };

        const onMouseUp = () => {
            isResizing.current = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [width]);

    const toggleSection = (id: string) => {
        setOpenSections(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSummary = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedSummary(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const getItemStateClass = (isActive: boolean, collapsedActive = false): string => {
        if (collapsedActive) return 'border border-[#2255a1] dark:border-[#f4a621] text-[#2255a1] dark:text-[#d3ddec]';
        if (isActive) return 'bg-white dark:bg-[#0f172a] border-[#2255a1] dark:border-[#f4a621] text-[#2255a1] dark:text-[#d3ddec]';
        return 'border-transparent hover:bg-white dark:hover:bg-[#0f172a] hover:border hover:border-[#2255a1] dark:hover:border-[#0f172a] text-slate-600 dark:text-[#94a3b8] dark:hover:text-[#d3ddec]';
    };

    const renderCountOrDelete = (item: FilterSidebarItem, sectionId: string, isActive: boolean) => {
        if (isIconOnly) return null;
        const isHovered = hoveredItemKey === item.key;
        if (isActive && item.editable && isHovered) {
            return (
                <span
                    className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); onDeleteItem?.(sectionId, item.key); }}
                    title="Удалить"
                >
                    <DeleteIcon />
                </span>
            );
        }
        if (item.count !== undefined) {
            return (
                <span className={`text-[11px] ${isActive ? 'text-[#2255a1] dark:text-[#d3ddec]' : 'text-slate-400'}`}>
                    {item.count}
                </span>
            );
        }
        return null;
    };

    const renderLabel = (item: FilterSidebarItem, isActive: boolean, medium = false) => {
        if (isIconOnly) {
            if (item.icon) return <span className="shrink-0">{item.icon}</span>;
            return (
                <span className="text-[10px] font-semibold text-center w-full">
                    {item.label.slice(0, 3)}
                </span>
            );
        }
        return (
            <span className={`text-[12px] truncate flex-1 ${isActive ? 'text-[#2255a1] dark:text-[#d3ddec]' : ''} ${medium ? 'font-medium' : ''}`}>
                {item.label}
            </span>
        );
    };

    const renderSummaryItem = (item: FilterSidebarItem, sectionId: string) => {
        const isActive = activeFilter === item.key;
        const isSummaryExpanded = expandedSummary.has(sectionId);
        const collapsedActive = isActive && !isSummaryExpanded;
        const isHovered = hoveredItemKey === item.key;

        return (
            <div
                key={item.key}
                className={[
                    'flex items-center cursor-pointer select-none transition-colors border-l-2',
                    isIconOnly ? 'px-2 py-[2px] justify-center' : 'px-2 py-[2px] justify-between',
                    getItemStateClass(isActive, collapsedActive),
                ].join(' ')}
                onClick={() => {
                    onChange(item.key);
                    setExpandedSummary(prev => {
                        const next = new Set(prev);
                        next.has(sectionId) ? next.delete(sectionId) : next.add(sectionId);
                        return next;
                    });
                }}
                onMouseEnter={() => setHoveredItemKey(item.key)}
                onMouseLeave={() => setHoveredItemKey(null)}
            >
                <div className="flex items-center gap-[5px] min-w-0 flex-1">
                    {isActive && item.editable && isHovered && !isIconOnly && (
                        <span className="shrink-0 text-slate-400 hover:text-[#2255a1]">
                            <PencilIcon />
                        </span>
                    )}
                    {!isCompact && !isIconOnly && (
                        <span className="shrink-0 text-slate-400 pointer-events-none">
                            <ChevronDown open={isSummaryExpanded} />
                        </span>
                    )}
                    {renderLabel(item, isActive, true)}
                </div>
                <div className="shrink-0 ml-1">
                    {renderCountOrDelete(item, sectionId, isActive)}
                </div>
            </div>
        );
    };

    const renderSubItem = (item: FilterSidebarItem, sectionId: string) => {
        const isActive = activeFilter === item.key;
        const isHovered = hoveredItemKey === item.key;

        return (
            <div
                key={item.key}
                className={[
                    'flex items-center justify-between cursor-pointer select-none py-[2px] transition-colors border-l-2',
                    isIconOnly ? 'px-2 justify-center' : isCompact ? 'px-2' : 'pl-6 pr-2',
                    getItemStateClass(isActive),
                ].join(' ')}
                onClick={() => onChange(item.key)}
                onMouseEnter={() => setHoveredItemKey(item.key)}
                onMouseLeave={() => setHoveredItemKey(null)}
            >
                <div className="flex items-center gap-[5px] min-w-0 flex-1">
                    {isActive && item.editable && isHovered && !isIconOnly && (
                        <span className="shrink-0 text-slate-400 hover:text-[#2255a1]">
                            <PencilIcon />
                        </span>
                    )}
                    {item.icon && <span className="shrink-0">{item.icon}</span>}
                    {renderLabel(item, isActive)}
                </div>
                <div className="shrink-0 ml-1">
                    {renderCountOrDelete(item, sectionId, isActive)}
                </div>
            </div>
        );
    };

    const isAddingSet = addingIn === 'header';

    return (
        <div
            className="relative flex flex-col shrink-0 overflow-hidden"
            style={{
                ...(fluid ? { width: '100%' } : { width, minWidth: MIN_WIDTH }),
                background: 'var(--stocks-filter-bg)',
                borderTopLeftRadius: '8px',
            }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-2 shrink-0" style={{ height: 34 }}>
                <div className="flex items-center gap-[5px] min-w-0">
                    <FilterListIcon width={14} height={14} className="shrink-0 text-slate-400" style={{ fill: 'currentColor' }} />
                    {!isCompact && !isIconOnly && (
                        <span className="text-[12px] text-slate-600 dark:text-[#d3ddec] truncate">
                            {isAddingSet ? 'Новый набор' : 'Фильтры'}
                        </span>
                    )}
                </div>
                {/* + button: grey by default, blue when 2+ items selected; becomes × when form is open */}
                {!isIconOnly && (
                    <button
                        className={[
                            'flex items-center justify-center w-[16px] h-[16px] rounded transition-colors shrink-0',
                            isAddingSet
                                ? 'text-slate-500 hover:text-red-500 hover:bg-red-50'
                                : (selectionCount ?? 0) >= 2
                                    ? 'text-[#2255a1] hover:bg-blue-100'
                                    : 'text-slate-400 hover:text-[#2255a1] hover:bg-blue-50',
                        ].join(' ')}
                        title={isAddingSet ? 'Отмена' : 'Добавить набор фильтров'}
                        onClick={() => setAddingIn(isAddingSet ? null : 'header')}
                    >
                        {isAddingSet ? <CloseIcon /> : <PlusIcon />}
                    </button>
                )}
            </div>

            {/* Inline form: add new filter set */}
            {isAddingSet && (
                <div className={'border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-[#0f172a]'}>
                    <InlineAddInput
                        placeholder="Имя набора..."
                        onConfirm={(value) => {
                            onAddSet?.(value);
                            setAddingIn(null);
                        }}
                        onCancel={() => setAddingIn(null)}
                    />
                </div>
            )}

            {/* Sections */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
                {sections.filter(s => !s.hidden).map(section => {
                    const isOpen = section.title ? openSections.has(section.id) : true;
                    const isSummaryExpanded = expandedSummary.has(section.id);
                    const isAddingHere = addingIn === section.id;

                    return (
                        <div key={section.id}>
                            {/* Section header */}
                            {section.title && (
                                <div
                                    className="flex items-center justify-between px-2 cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-[#0f172a] transition-colors"
                                    style={{ height: 34 }}
                                    onClick={() => toggleSection(section.id)}
                                >
                                    <div className="flex items-center gap-[5px] min-w-0">
                                        {!isCompact && !isIconOnly && (
                                            <span className="shrink-0 text-slate-400">
                                                <ChevronDown open={isOpen} />
                                            </span>
                                        )}
                                        {!isIconOnly && (
                                            <span className={`text-[11px] font-semibold uppercase tracking-wide truncate text-slate-400 dark:text-slate-500`}>
                                                {section.title}
                                            </span>
                                        )}
                                    </div>
                                    {!isCompact && !isIconOnly && section.addButton && (
                                        <button
                                            className={[
                                                'flex items-center justify-center w-[14px] h-[14px] rounded transition-colors shrink-0',
                                                isAddingHere
                                                    ? 'text-slate-500 hover:text-red-500'
                                                    : 'text-slate-400 hover:text-[#2255a1]',
                                            ].join(' ')}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setAddingIn(isAddingHere ? null : section.id);
                                                // ensure section is open when adding
                                                if (!isOpen) toggleSection(section.id);
                                            }}
                                            title={isAddingHere ? 'Отмена' : `Добавить в ${section.title}`}
                                        >
                                            {isAddingHere ? <CloseIcon /> : <PlusIcon />}
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Summary item */}
                            {section.summaryItem && renderSummaryItem(section.summaryItem, section.id)}

                            {/* Inline add-item form (appears first in section when open) */}
                            {isAddingHere && isOpen && (
                                <InlineAddInput
                                    placeholder={`Добавить в ${section.title ?? 'раздел'}...`}
                                    onConfirm={(value) => {
                                        onAddItem?.(section.id, value);
                                        setAddingIn(null);
                                    }}
                                    onCancel={() => setAddingIn(null)}
                                />
                            )}

                            {/* Sub-items */}
                            {isSummaryExpanded && isOpen && section.items.map(item => renderSubItem(item, section.id))}
                            {!section.summaryItem && isOpen && section.items.map(item => renderSubItem(item, section.id))}
                        </div>
                    );
                })}
            </div>

            {/* Drag handle (only in desktop pinned mode) */}
            {!fluid && (
                <div
                    style={{
                        position: 'absolute', top: 0, right: 0, width: 4, height: '100%',
                        cursor: 'col-resize', zIndex: 10,
                        backgroundColor: 'transparent',
                        transition: 'background-color 0.15s',
                    }}
                    title="Изменить ширину"
                    onMouseDown={onMouseDown}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--hover-dim)')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                />
            )}
        </div>
    );
};

export default StocksFilterSidebar;
